// API-kutsut kävijäpolulla: konfiguraation haku ja suostumuksen tallennus.
// apiBase on oman domainin/CDN-proxyn osoite (pelisääntö 1) — ei koskaan
// suoraan *.supabase.co.

import type { Choices, CmpConfig } from './types';
import { getProofId, storeConsentId } from './storage';
import { bannerTextHash } from './text-hash';

export { bannerTextHash, canonicalTextsString, legacy32Hash, sha256Hex } from './text-hash';

/**
 * Hakee bannerin konfiguraation: ensin julkaisussa kirjoitettu staattinen
 * /config/{domain}.json (ei tietokantakyselyä eikä cold startia, V1-T5),
 * ja jos sitä ei ole (esim. sivustoa ei ole vielä julkaistu uudella
 * julkaisupolulla), fallbackina get-banner-config-funktio.
 */
export function fetchBannerConfig(apiBase: string, domain: string): Promise<CmpConfig> {
  return fetch(apiBase + '/config/' + encodeURIComponent(domain) + '.json')
    .then(function (r) {
      if (!r.ok) throw new Error('static config unavailable (' + r.status + ')');
      return r.json();
    })
    .catch(function () {
      return fetch(apiBase + '/get-banner-config?domain=' + encodeURIComponent(domain)).then(function (r) {
        return r.json();
      });
    });
}

/**
 * Lähettää suostumustapahtuman (save-consent) ja tallentaa vastauksen
 * todistustunnuksen. Virheet vain logataan — banneri ei saa kaatua, vaikka
 * tallennus epäonnistuisi.
 */
export function sendConsent(
  apiBase: string,
  domain: string,
  config: CmpConfig,
  lang: string,
  choices: Choices,
  action: string,
  onDone?: (consentId: string) => void,
): void {
  const texts = config.texts[lang] || config.texts['fi'] || {};
  // Tekstien tiiviste lasketaan ennen lähetystä (SHA-256, V2-T9). Laskenta on
  // asynkroninen (Web Crypto), joten se tehdään ketjun alussa — valinta on jo
  // sovellettu ja tallennettu selaimeen, joten kävijä ei odota tätä.
  bannerTextHash(texts)
    .then(function (textHash) {
      return fetch(apiBase + '/save-consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: config.site_id,
          // Payload-kenttä pysyy nimellä visitor_id, vaikka selainavain on
          // cmp_proof_id (V2-T1, PL-5): consent_records.visitor_id on
          // append-only-lokin sarake, jonka nimeäminen katkaisisi vanhojen ja
          // uusien rivien vertailukelpoisuuden.
          visitor_id: getProofId(),
          consent_action: action,
          consent_choices: choices,
          banner_config_version: config.version || 1,
          banner_language_shown: lang,
          page_url: location.href,
          user_agent_category: /Mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
          full_banner_text_hash: textHash.hash,
          full_banner_text_hash_algo: textHash.algo,
        }),
      });
    })
    .then(function (r) {
      return r.json();
    })
    .then(function (data) {
      console.log('CMP: save-consent response', data);
      if (data && data.consent_id) {
        storeConsentId(domain, data.consent_id);
        if (onDone) onDone(data.consent_id);
      } else {
        console.warn('CMP: no consent_id in response', data);
      }
    })
    .catch(function (e) {
      console.warn('CMP: consent save failed', e);
    });
}
